import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Users, Coins, Clock, ShieldCheck, BarChart3, Globe2, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/app/AppShell';
import { useEmployees } from '@/lib/employees';
import { useDepartments } from '@/lib/departments';
import { useStageSummary } from '@/lib/hiring';
import { useGoals } from '@/lib/performance';
import { useMyCompany } from '@/lib/companies';
import { countryFlag } from '@/lib/countries';

/**
 * Cross-section reports view. Pulls data already loaded by the
 * other modules — keeps Reports as a read-only aggregation surface.
 */
export function ReportsPage() {
  const { t } = useTranslation();
  const company = useMyCompany();
  const employees = useEmployees({});
  const hiringSummary = useStageSummary();
  const goals = useGoals();

  const byCountry = (employees.data ?? []).reduce<Record<string, number>>((acc, e) => {
    acc[e.countryCode] = (acc[e.countryCode] ?? 0) + 1;
    return acc;
  }, {});

  const departments = useDepartments();
  const deptName = Object.fromEntries((departments.data ?? []).map((d) => [d.id, d.name]));
  const byDepartment = (employees.data ?? []).reduce<Record<string, number>>((acc, e) => {
    const k = e.departmentId ?? '__none';
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});

  const headcount = (employees.data ?? []).length;
  const goalsOnTrack = (goals.data ?? []).filter((g) => g.status === 'on_track' || g.status === 'done').length;
  const goalsAtRisk = (goals.data ?? []).filter((g) => g.status === 'at_risk').length;
  const pipelineCount = hiringSummary.data
    ? hiringSummary.data.applied + hiringSummary.data.screen + hiringSummary.data.interview + hiringSummary.data.offer
    : 0;

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto space-y-6">
        <header>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('reports.eyebrow')}</p>
          <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight">{t('reports.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('reports.subtitle')}</p>
          <Link to="/app/analytics" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
            <BarChart3 className="h-4 w-4" />{t('reports.toAnalytics', { defaultValue: 'Open HR analytics — charts, turnover & AI insights →' })}
          </Link>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile icon={Users} accent="from-indigo-500 to-fuchsia-500" label={t('reports.headcount')} value={String(headcount)} />
          <StatTile icon={Globe2} accent="from-sky-500 to-indigo-500" label={t('reports.countries')} value={String(Object.keys(byCountry).length)} />
          <Link to="/app/hiring" className="block">
            <StatTile icon={BarChart3} accent="from-emerald-500 to-sky-500" label={t('reports.pipeline')} value={String(pipelineCount)} />
          </Link>
          <StatTile icon={Coins} accent="from-fuchsia-500 to-amber-500" label={t('reports.currency')} value={company.data?.reportingCurrency ?? '—'} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <section className="lg:col-span-2 rounded-2xl border border-border bg-card p-6 shadow-soft">
            <h2 className="text-lg font-semibold mb-4">{t('reports.headcountByCountry')}</h2>
            {Object.entries(byCountry).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('reports.empty')}</p>
            ) : (
              <ul className="space-y-3">
                {Object.entries(byCountry)
                  .sort(([, a], [, b]) => b - a)
                  .map(([code, count]) => {
                    const pct = headcount > 0 ? Math.round((count / headcount) * 100) : 0;
                    return (
                      <li key={code}>
                        <Link
                          to={`/app/people?country=${code}`}
                          className="flex items-center gap-3 rounded-lg hover:bg-muted/50 transition-colors -mx-2 px-2 py-0.5"
                          title={t('reports.viewPeople', { defaultValue: 'View people' })}
                        >
                          <span className="text-base w-7">{countryFlag(code)}</span>
                          <span className="text-sm flex-1 truncate">{code}</span>
                          <div className="w-40 h-1.5 bg-muted rounded-full overflow-hidden">
                            <motion.div
                              className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500"
                              initial={{ width: 0 }}
                              animate={{ width: `${pct}%` }}
                              transition={{ duration: 0.6 }}
                            />
                          </div>
                          <span className="num text-xs font-semibold w-6 text-end">{count}</span>
                        </Link>
                      </li>
                    );
                  })}
              </ul>
            )}
          </section>

          <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <h2 className="text-lg font-semibold mb-4">{t('reports.performance')}</h2>
            <div className="space-y-3 text-sm">
              <Link
                to="/app/performance"
                className="flex items-center justify-between rounded-lg hover:bg-muted/50 transition-colors -mx-2 px-2 py-1"
              >
                <span className="text-muted-foreground inline-flex items-center gap-1.5">
                  <Target className="h-3.5 w-3.5" /> {t('reports.goalsOnTrack')}
                </span>
                <span className="num font-semibold text-emerald-600">{goalsOnTrack}</span>
              </Link>
              <Link
                to="/app/performance"
                className="flex items-center justify-between rounded-lg hover:bg-muted/50 transition-colors -mx-2 px-2 py-1"
              >
                <span className="text-muted-foreground inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" /> {t('reports.goalsAtRisk')}
                </span>
                <span className="num font-semibold text-amber-600">{goalsAtRisk}</span>
              </Link>
              <Link
                to="/app/compliance"
                className="flex items-center justify-between rounded-lg hover:bg-muted/50 transition-colors -mx-2 px-2 py-1"
              >
                <span className="text-muted-foreground inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5" /> {t('reports.complianceState')}
                </span>
                <span className="text-xs font-semibold text-foreground/70">{t('reports.live')}</span>
              </Link>
            </div>
          </section>
        </div>

        {Object.keys(byDepartment).length > 0 && (
          <section className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <h2 className="text-lg font-semibold mb-4">{t('reports.headcountByDepartment', { defaultValue: 'Headcount by department' })}</h2>
            <ul className="space-y-3">
              {Object.entries(byDepartment)
                .sort(([, a], [, b]) => b - a)
                .map(([id, count]) => {
                  const pct = headcount > 0 ? Math.round((count / headcount) * 100) : 0;
                  const label = id === '__none' ? t('reports.noDepartment', { defaultValue: 'Unassigned' }) : (deptName[id] ?? id.slice(0, 8));
                  const inner = (
                    <>
                      <span className="text-sm flex-1 truncate">{label}</span>
                      <div className="w-40 h-1.5 bg-muted rounded-full overflow-hidden">
                        <motion.div className="h-full bg-gradient-to-r from-emerald-500 to-sky-500" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} />
                      </div>
                      <span className="num text-xs font-semibold w-6 text-end">{count}</span>
                    </>
                  );
                  return (
                    <li key={id}>
                      {id === '__none' ? (
                        <div className="flex items-center gap-3 -mx-2 px-2 py-0.5">{inner}</div>
                      ) : (
                        <Link to={`/app/people?department=${id}`} className="flex items-center gap-3 rounded-lg hover:bg-muted/50 transition-colors -mx-2 px-2 py-0.5">{inner}</Link>
                      )}
                    </li>
                  );
                })}
            </ul>
          </section>
        )}
      </div>
    </AppShell>
  );
}

function StatTile({
  icon: Icon, accent, label, value,
}: { icon: React.ComponentType<{ className?: string }>; accent: string; label: string; value: string }) {
  return (
    <motion.div
      whileHover={{ y: -3 }}
      className="relative overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-soft"
    >
      <div className={`pointer-events-none absolute -top-12 -end-12 h-32 w-32 rounded-full bg-gradient-to-br ${accent} opacity-10 blur-2xl`} />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
          <p className="num mt-2 text-3xl font-bold tracking-tight">{value}</p>
        </div>
        <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-glow`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </motion.div>
  );
}

